import { Box, TableCell, Typography } from "@mui/material";
import { FirewatchChip } from "~/components/FirewatchRow/FirewatchChip";
import { FindingException, Icon_Ids, ScoreState, Tag } from "~/operations";
import { NotificationImportant as AdvisoryIcon } from "@mui/icons-material";
import { DynamicIcon } from "~/components/icons/DynamicIcon";
import { ExceptionChip } from "~/components";
import { ClampWithTooltip } from "~/components/ClampWithTooltip/ClampWithTooltip";
import { EntityInfoAsset } from "~/components/FirewatchPage";
import { EXCEPTIONS_SCORE_STATES } from "~/pages/findings/Exceptions/constants";
import { ExceptionTooltipContent } from "~/components/exceptions/ExceptionTooltipContent/ExceptionTooltipContent";

type FindingTitleCellProps = {
  id: string;
  title: string;
  asset?: EntityInfoAsset | null;
  tags?: Array<Tag>;
  state?: ScoreState;
  isPendingException?: boolean;
  onPendingExceptionClick?: () => void;
  "data-testid"?: string;
  iconId?: Icon_Ids | null | undefined;
  exception?: FindingException | null;
};

export const TitleCell = ({
  state,
  isPendingException = false,
  onPendingExceptionClick,
  id,
  title,
  asset,
  tags = [],
  iconId,
  exception,
  ...props
}: FindingTitleCellProps) => {
  const hasException =
    (state && EXCEPTIONS_SCORE_STATES.includes(state)) || isPendingException;

  const orderedTags = [...tags].sort((a, b) => a.key.localeCompare(b.key));
  const prefix = asset ? `${id} on ${asset.name}: ` : `${id}: `;

  const exceptionTooltipProps = {
    title: <ExceptionTooltipContent exception={exception ?? null} />,
  };

  return (
    <TableCell data-testid={props["data-testid"]}>
      <Box display="flex" alignItems="center" gap={1}>
        {iconId && <DynamicIcon iconId={iconId} defaultIcon={AdvisoryIcon} />}
        <ClampWithTooltip>
          <Typography
            fontSize="14px"
            data-testid="finding-id"
            sx={{ color: hasException ? "text.secondary" : "text.primary" }}
          >
            {id && (
              <Typography fontSize="14px" fontWeight="bold" variant="caption">
                {prefix}
              </Typography>
            )}
            {title}
          </Typography>
        </ClampWithTooltip>
      </Box>

      {(hasException || tags.length > 0) && (
        <Box
          display="flex"
          gap={1}
          mt={hasException || (tags?.length || 0) > 0 ? 1 : 0}
          alignItems="center"
        >
          {hasException && (
            <ExceptionChip
              isRiskAccepted={state === ScoreState.RiskAccepted}
              isDisabled={state === ScoreState.Disabled}
              isFalsePositive={state === ScoreState.FalsePositive}
              isWorkaround={state === ScoreState.Workaround}
              onPendingExceptionClick={
                isPendingException ? onPendingExceptionClick : undefined
              }
              TooltipProps={exceptionTooltipProps}
            />
          )}
          {orderedTags?.map((tag) => (
            <FirewatchChip key={tag.key} tag={tag} />
          ))}
        </Box>
      )}
    </TableCell>
  );
};
